/**
 * PersonalSection — renders the 4 personal-info fields.
 *
 * Fields: firstName, lastName, age, gender.
 *
 * Used by:
 *   - SignupPage  (step 1 of the wizard)
 *   - ProfilePage (top section of the single-page edit form)
 *
 * The caller owns the useForm instance (one form per page). This
 * component is a pure presentational layer over react-hook-form's
 * Controller — it reads `control` and `errors` from props and registers
 * its fields against them. That way SignupPage's step-validation
 * (`trigger(['firstName', 'lastName', 'age', 'gender'])`) and
 * ProfilePage's full-form submit both work without this component
 * needing to know which page it's in.
 *
 * Props:
 *   - control  : react-hook-form `control` from useForm()
 *   - errors   : react-hook-form `formState.errors` from useForm()
 *   - t        : i18next translator (passed in so the component doesn't
 *                re-call useTranslation per render)
 */

import {
  Stack,
  TextField,
  FormControl,
  FormLabel,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { Controller } from 'react-hook-form';

import { GENDER_CODES } from './constants';

export default function PersonalSection({ control, errors, t }) {
  return (
    <Stack spacing={2.5}>
      {/* First name + last name side-by-side on desktop, stacked on mobile */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('signup.fields.firstName.label')}
              placeholder={t('signup.fields.firstName.placeholder')}
              fullWidth
              autoComplete="given-name"
              error={!!errors.firstName}
              // Reserve helperText line height even when no error so the
              // row doesn't visually jump when errors appear/disappear.
              helperText={errors.firstName?.message ?? ' '}
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('signup.fields.lastName.label')}
              placeholder={t('signup.fields.lastName.placeholder')}
              fullWidth
              autoComplete="family-name"
              error={!!errors.lastName}
              helperText={errors.lastName?.message ?? ' '}
            />
          )}
        />
      </Stack>

      <Controller
        name="age"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('signup.fields.age.label')}
            placeholder={t('signup.fields.age.placeholder')}
            fullWidth
            inputMode="numeric"
            autoComplete="off"
            // Age is bidi-neutral but explicit LTR keeps the cursor sane
            // when the surrounding UI is RTL. Persian/Arabic digits typed
            // here get normalized by the Zod schema before submission.
            inputProps={{ dir: 'ltr', style: { textAlign: 'left' }, maxLength: 3 }}
            error={!!errors.age}
            helperText={errors.age?.message ?? ' '}
          />
        )}
      />

      <Controller
        name="gender"
        control={control}
        render={({ field }) => (
          <FormControl error={!!errors.gender} component="fieldset">
            <FormLabel component="legend">
              {t('signup.fields.gender.label')}
            </FormLabel>
            <RadioGroup row {...field}>
              {GENDER_CODES.map((code) => (
                <FormControlLabel
                  key={code}
                  value={code}
                  control={<Radio />}
                  label={t(`signup.fields.gender.${code}`)}
                />
              ))}
            </RadioGroup>
            <FormHelperText>{errors.gender?.message ?? ' '}</FormHelperText>
          </FormControl>
        )}
      />
    </Stack>
  );
}
